import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { ModalProvider } from 'react-modal-hook';
import { Navigate, Outlet } from 'react-router-dom';
import { SettingsLayout } from 'src/layouts/SettingsLayout/SettingsLayout';
import { WizardLayout } from 'src/layouts/WizardLayout';
import { VoiceChannelContextProvider } from 'src/provider/VoiceChannel';
import { DriveContextProvider } from 'src/sections/@dashboard/drive/DriveContext';
import { useRoutes } from 'src/services/sentry';
import { SocketProvider } from 'src/socket/provider';
import ThemeProvider from 'src/theme';
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';

import { PATH_AFTER_LOGIN } from 'src/Router/paths';
import { AfterSignInGuard } from 'src/auth/AfterSignInGuard';
import { ThemeSettings } from 'src/components/settings/ThemeSettings';
import { CollaborateBridgeInitializer } from 'src/services/CollaborateNativeBridge';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { NavAI } from '../layouts/DashboardLayout/NavAI/NavAI';
import { NavCollaborate } from '../layouts/DashboardLayout/NavCollaborate';
import { NavCommunicate } from '../layouts/DashboardLayout/NavCommunicate';
import CompactLayout from '../layouts/compact';
import { NavbarContextProvider } from '../provider/NavbarContext';
import { VoiceChannelEventsHandler } from '../provider/VoiceChannel/VoiceChannelEventsHandler';
import { GuestGatheringLobby } from '../sections/@dashboard/gathering/GuestGatheringLobby';
import {
  AIPage,
  AIScheduleSummariesPage,
  AISchedulesPage,
  BlankPage,
  BoardPage,
  CalendarPage,
  ChatPage,
  ComingSoonPage,
  DocsPage,
  DocsPublicPage,
  DocsTrashPage,
  DrivePage,
  DriveTrashPage,
  EmptyBoardsPage,
  EnableTwoFactorPage,
  ForceChangePasswordPage,
  ForgotPasswordPage,
  GuestGatheringPage,
  LoginPage,
  LoginWithTwoFactorPage,
  MaintenancePage,
  Page403,
  Page404,
  Page500,
  PaymentPage,
  PermissionDeniedPage,
  PlansPage,
  PublicDrivePage,
  RegisterPage,
  ResetPasswordPage,
  SettingsUserLanguage,
  SettingsUserProfilePage,
  SettingsUserSecurityPage,
  SettingsWorkspaceBillingPage,
  SettingsWorkspaceGeneralPage,
  SettingsWorkspaceMembersPage,
  SettingsWorkspaceRolesPage,
  SettingsWorkspaceTeamsPage,
  TableViewPage,
  TodosPage,
  VerifyAccountPage,
  WizardCreateWorkspacePage,
  WizardInviteMembersPage,
  WorkspaceInvitePage,
  WorkspaceJoiningPage,
  ZenPage,
} from './elements';

import { WorkspaceInfoStoreProvider } from '@modules/workspace/providers/WorkspaceInfo';
import SnackbarProvider from 'src/components/snackbar';
import { MainAppProviders, Providers } from './providers';

export const Pages = ({ location }: { location: Partial<Location> }) =>
  useRoutes(
    [
      {
        path: '/collaborate-app-session',
        element: <CollaborateBridgeInitializer />,
      },
      {
        path: '/gathering/lobby/:externalLink',
        element: (
          <ThemeProvider themeMode="dark">
            <ThemeSettings>
              <ModalProvider>
                <GuestGatheringLobby />
              </ModalProvider>
            </ThemeSettings>
          </ThemeProvider>
        ),
      },
      {
        path: '/gathering/join/:id',
        element: (
          <ThemeProvider themeMode="dark">
            <ThemeSettings>
              <SocketProvider>
                <WorkspaceInfoStoreProvider>
                  <SnackbarProvider>
                    <VoiceChannelContextProvider>
                      <VoiceChannelEventsHandler />
                      <ModalProvider>
                        <GuestGatheringPage />
                      </ModalProvider>
                    </VoiceChannelContextProvider>
                  </SnackbarProvider>
                </WorkspaceInfoStoreProvider>
              </SocketProvider>
            </ThemeSettings>
          </ThemeProvider>
        ),
      },
      {
        path: '/shared-content/:token',
        element: <PublicDrivePage />,
      },
      {
        path: '/public-doc/:token',
        element: <DocsPublicPage />,
      },
      {
        path: '/public-doc/:token/:childToken',
        element: <DocsPublicPage />,
      },
      {
        path: '/',
        element: <Navigate to="/login" replace />,
        index: true,
      },
      {
        path: 'login',
        element: (
          <GuestGuard>
            <SocketProvider namespace="public">
              <LoginPage />
            </SocketProvider>
          </GuestGuard>
        ),
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <GoogleReCaptchaProvider reCaptchaKey="6Ld841QqAAAAAEutiEUfcjaPfLs2xcwwHcPcibrp">
              <ModalProvider>
                <RegisterPage />
              </ModalProvider>
            </GoogleReCaptchaProvider>
          </GuestGuard>
        ),
      },
      {
        path: 'workspaceInvite/:workspaceInvite',
        element: <WorkspaceInvitePage />,
      },
      {
        path: 'verify-email',
        element: <VerifyAccountPage />,
      },
      {
        path: 'forgot-password',
        element: (
          <GuestGuard>
            <ModalProvider>
              <ForgotPasswordPage />
            </ModalProvider>
          </GuestGuard>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <GuestGuard>
            <ResetPasswordPage />
          </GuestGuard>
        ),
      },
      {
        path: 'two-factor',
        element: (
          <GuestGuard>
            <LoginWithTwoFactorPage />
          </GuestGuard>
        ),
      },
      {
        path: 'wizard',
        element: <WizardLayout />,
        children: [
          {
            path: '',
            index: true,
            // Will back in the future. Now is goes to new
            // element: (
            //   <AuthGuard>
            //     <WizardWelcomePage />
            //   </AuthGuard>
            // ),
            element: (
              <AuthGuard>
                <WizardCreateWorkspacePage />
              </AuthGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <AuthGuard>
                <WizardCreateWorkspacePage />
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: 'need-change-password',
        element: (
          <AuthGuard>
            <ForceChangePasswordPage />
          </AuthGuard>
        ),
      },
      {
        path: 'need-enable-two-factor',
        element: (
          <AuthGuard>
            <EnableTwoFactorPage />
          </AuthGuard>
        ),
      },

      {
        element: <CompactLayout />,
        children: [
          { path: 'coming-soon', element: <ComingSoonPage /> },
          { path: 'maintenance', element: <MaintenancePage /> },
          { path: 'server-error', element: <Page500 /> },
          { path: 'not-found', element: <Page404 /> },
          { path: 'forbidden', element: <Page403 /> },
        ],
      },

      {
        path: '/signed-in',
        element: <AfterSignInGuard />,
      },

      {
        path: 'workspaceJoining/:workspaceInvite',
        element: <WorkspaceJoiningPage />,
      },

      // Dashboard
      {
        path: '/:accountIndex',
        element: (
          <AuthGuard>
            <Providers>
              <Outlet />
            </Providers>
          </AuthGuard>
        ),
        children: [
          { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
          {
            path: 'signed-in',
            element: <AfterSignInGuard />,
          },
          {
            path: 'wizard',
            element: <WizardLayout />,
            children: [
              {
                path: 'plans',
                element: (
                  <AuthGuard>
                    <PlansPage />
                  </AuthGuard>
                ),
              },
              {
                path: 'payment',
                element: (
                  <AuthGuard>
                    <PaymentPage />
                  </AuthGuard>
                ),
              },
              {
                path: 'invite',
                element: (
                  <AuthGuard>
                    <WizardInviteMembersPage />
                  </AuthGuard>
                ),
              },
            ],
          },
          {
            path: '',
            element: (
              <MainAppProviders>
                <Outlet />
              </MainAppProviders>
            ),
            children: [
              {
                path: 'zen',
                element: (
                  <ZenPage>
                    <Outlet />
                  </ZenPage>
                ),
                children: [
                  {
                    path: 'todo',
                    children: [
                      {
                        path: ':id',
                        element: <TodosPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: '',
                element: (
                  <NavbarContextProvider area="collaborate">
                    <DashboardLayout>
                      <NavCollaborate />
                    </DashboardLayout>
                  </NavbarContextProvider>
                ),
                children: [
                  {
                    path: 'boards',
                    element: <EmptyBoardsPage />,
                  },
                  {
                    path: 'boards/:boardId',
                    element: <BoardPage />,
                    children: [
                      { path: ':tableId', element: <TableViewPage /> },
                      { path: ':tableId/:viewId', element: <TableViewPage /> },
                    ],
                  },
                  {
                    path: 'todo',
                    children: [
                      {
                        path: '',
                        element: <TodosPage />,
                      },
                      {
                        path: ':id',
                        element: <TodosPage />,
                      },
                    ],
                  },
                  {
                    path: 'docs',
                    children: [
                      { path: ':id', element: <DocsPage /> },
                      { path: 'trash', element: <DocsTrashPage /> },
                    ],
                  },
                ],
              },
              {
                path: '',
                element: (
                  <NavbarContextProvider area="communicate">
                    <DashboardLayout>
                      <NavCommunicate />
                    </DashboardLayout>
                  </NavbarContextProvider>
                ),
                children: [
                  {
                    path: 'chat',
                    children: [
                      { path: '', element: <ChatPage />, index: true },
                      { path: 'new', element: <ChatPage /> },
                      { path: ':conversationKey', element: <ChatPage /> },
                      {
                        path: ':rootConversationKey/subchannel/:conversationKey',
                        element: <ChatPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: '',
                element: (
                  <NavbarContextProvider area="ai">
                    <DashboardLayout>
                      <NavAI />
                    </DashboardLayout>
                  </NavbarContextProvider>
                ),
                children: [
                  {
                    path: 'ai',
                    children: [
                      { path: '', element: <AIPage />, index: true },
                      { path: ':id', element: <AIPage /> },
                      {
                        path: 'schedule',
                        element: <AISchedulesPage />,
                      },
                      {
                        path: 'schedule/:id',
                        element: <AIScheduleSummariesPage />,
                      },
                    ],
                  },
                ],
              },

              {
                path: '',
                element: (
                  <NavbarContextProvider area="collaborate">
                    <DriveContextProvider>
                      <DashboardLayout>
                        <NavCollaborate />
                      </DashboardLayout>
                    </DriveContextProvider>
                  </NavbarContextProvider>
                ),
                children: [
                  {
                    path: 'drive',
                    children: [
                      {
                        path: 'f',
                        children: [
                          { element: <DrivePage />, index: true },
                          { path: ':folderId', element: <DrivePage /> },
                        ],
                      },
                      {
                        path: 'shared-with-me',
                        children: [
                          {
                            element: (
                              <DrivePage searchFilter="shared-with-me" />
                            ),
                            index: true,
                          },
                          {
                            path: ':folderId',
                            element: (
                              <DrivePage searchFilter="shared-with-me" />
                            ),
                          },
                        ],
                      },
                      {
                        path: 'starred',
                        element: <DrivePage searchFilter="starred" />,
                      },
                      {
                        path: 'trash',
                        element: <DriveTrashPage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              {
                path: 'workspace',
                element: <Outlet />,
                children: [
                  {
                    path: 'general',
                    element: <SettingsWorkspaceGeneralPage />,
                  },
                  {
                    path: 'members',
                    element: <SettingsWorkspaceMembersPage />,
                  },
                  {
                    path: 'teams',
                    element: <SettingsWorkspaceTeamsPage />,
                  },
                  {
                    path: 'roles',
                    element: <SettingsWorkspaceRolesPage />,
                  },
                  {
                    path: 'billing',
                    element: <SettingsWorkspaceBillingPage />,
                  },
                ],
              },
              {
                path: 'user',
                element: <Outlet />,
                children: [
                  { path: 'account', element: <SettingsUserProfilePage /> },
                  // {
                  //   path: 'notifications',
                  //   element: <SettingsUserNotificationsPage />,
                  // },
                  {
                    path: 'security',
                    element: <SettingsUserSecurityPage />,
                  },
                  {
                    path: 'language',
                    element: <SettingsUserLanguage />,
                  },
                ],
              },
            ],
          },
          {
            path: '',
            element: (
              <NavbarContextProvider area="collaborate">
                <DashboardLayout>
                  {/* <NavCommunicate /> */}
                  <span />
                </DashboardLayout>
              </NavbarContextProvider>
            ),
            children: [
              { path: 'calendar', element: <CalendarPage /> },
              { path: 'permission-denied', element: <PermissionDeniedPage /> },
              { path: 'blank', element: <BlankPage /> },
            ],
          },
        ],
      },
    ],
    location,
  );
